import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, CircularProgress, makeStyles } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import { Button } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { FileUploadZone } from 'modules/MileageDeclarations/Components/FileUploadZone';
import { useSnackbar } from 'notistack';
import { useVehicleBulkImport } from '../hooks/useVehicleBulkImport';
import ValidationErrorsTable from './ValidationErrorFile';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  downloadButton: {
    marginBottom: theme.spacing(2),
  },
}));

interface VehicleBulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  fleetId: string;
}

export function VehicleBulkUploadDialog({ open, onClose, fleetId }: VehicleBulkUploadDialogProps): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [fileError, setFileError] = useState('');
  const [vehicleValidationResult, setVehicleValidationResult] = useState<{
    validation?: { issues: any[] };
    validRows?: number;
    invalidRows?: number;
  } | null>(null);
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  const [fileID, setFileID] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    downloadTemplate: originalDownloadTemplate,
    isUploading,
    validateFileType,
    uploadAndValidateFile,
    confirmImport,
  } = useVehicleBulkImport({ fleetId });

  // Override downloadTemplate to handle base64 and download
  const downloadTemplate = () => {
    originalDownloadTemplate();
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleFileSelected = async (selectedFile: File | null) => {
    setFileError('');
    if (!selectedFile) {
      setFile(null);
      return;
    }

    const error = validateFileType(selectedFile);
    if (error) {
      setFileError(error);
      setFile(null);
      return;
    }

    setFile(selectedFile);
    try {
      const result = await uploadAndValidateFile(selectedFile);
      setVehicleValidationResult(result);
      setShowConfirmButton(true);
      setFileID(result?.fileID ?? null);
    } catch (e) {
      setShowConfirmButton(false);
    }
  };

  const handleConfirm = async () => {
    if (!fileID) {
      enqueueSnackbar(t('No file uploaded for confirmation.'), { variant: 'error' });
      return;
    }

    setIsConfirming(true);
    try {
      await confirmImport(fileID);
      enqueueSnackbar(t('Vehicles uploaded successfully!'), { variant: 'success' });
      // Optionally: show result summary to user here
      handleClose();
    } catch (error) {
      setIsConfirming(false);
      enqueueSnackbar(t('Upload confirmation failed.'), { variant: 'error' });
    }
  };

  const handleClose = () => {
    setFile(null);
    setFileKey((prev) => prev + 1);
    // setFileError('');
    setVehicleValidationResult(null);
    setShowConfirmButton(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Upload Vehicles')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetApp />}
          onClick={downloadTemplate}
          disabled={isUploading}
          fullWidth
          className={classes.downloadButton}
        >
          {t('Download Template')}
        </Button>
        <FileUploadZone file={file} fileKey={fileKey} fileError={fileError} onFileSelected={handleFileSelected} />
        {isUploading && <LinearProgress style={{ marginTop: 16 }} />}
      </DialogContent>
      {vehicleValidationResult &&
        vehicleValidationResult.validation &&
        ((Array.isArray(vehicleValidationResult.validation.issues) &&
          vehicleValidationResult.validation.issues.length > 0) ||
          (typeof vehicleValidationResult.validRows === 'number' &&
            vehicleValidationResult.validRows > 0 &&
            vehicleValidationResult.invalidRows === 0)) && <ValidationErrorsTable data={vehicleValidationResult} />}
      <DialogActions>
        <Button variant="text" color="primary" onClick={handleClose} disabled={isUploading}>
          {t('Cancel')}
        </Button>
        {file && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={
              Boolean(isUploading) ||
              isConfirming ||
              !showConfirmButton ||
              !!(vehicleValidationResult && vehicleValidationResult.validRows === 0)
            }
          >
            {isConfirming ? (
              <CircularProgress size={24} color="inherit" />
            ) : isUploading ? (
              t('Validating...')
            ) : (
              t('Confirm')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
